import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { ArrowRight, TrendingUp, TrendingDown, Minus, CheckCircle2, AlertTriangle, Info, Download } from "lucide-react";
import SEO from "../components/SEO";

const SECTORS = ["Meccanica", "Automotive", "Alimentare", "Beverage", "Tessile", "Chimica", "Farmaceutico", "Vetro", "Ceramica", "Fonderia", "Software", "Servizi B2B", "Retail", "Carta", "Altro"];

export default function Valutazione() {
  const [form, setForm] = useState({
    company_name: "", sector: "Meccanica", region: "", year_founded: "", employees: 0,
    revenue_current_eur_m: 0, revenue_prev_eur_m: 0, revenue_prev2_eur_m: 0,
    ebitda_current_eur_m: 0, ebitda_prev_eur_m: 0, ebitda_prev2_eur_m: 0,
    net_debt_eur_m: 0, owner_age: "", top3_customer_concentration_pct: 40,
    founder_dependency_score: 3, email: "", contact_name: "", consent_marketing: false,
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const num = (v) => v === "" || v === null ? "" : Number(v);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const payload = { ...form };
      // Coerce numerics
      ["employees", "revenue_current_eur_m", "revenue_prev_eur_m", "revenue_prev2_eur_m",
       "ebitda_current_eur_m", "ebitda_prev_eur_m", "ebitda_prev2_eur_m", "net_debt_eur_m",
       "owner_age", "year_founded", "top3_customer_concentration_pct", "founder_dependency_score"].forEach(k => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      // Required must not be null
      if (payload.owner_age === null) delete payload.owner_age;
      if (payload.year_founded === null) delete payload.year_founded;
      if (!payload.revenue_prev_eur_m) delete payload.revenue_prev_eur_m;
      if (!payload.revenue_prev2_eur_m) delete payload.revenue_prev2_eur_m;
      if (!payload.ebitda_prev_eur_m) delete payload.ebitda_prev_eur_m;
      if (!payload.ebitda_prev2_eur_m) delete payload.ebitda_prev2_eur_m;
      const r = await api.post("/platform/valuation", payload);
      setResult(r.data);
      setStatus("done");
      setTimeout(() => window.scrollTo({ top: 700, behavior: "smooth" }), 100);
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <main data-testid="valutazione-page">
      <SEO
        title="Quanto vale la tua azienda"
        description="Motore di valutazione istantanea per PMI italiane. In 10 minuti: EBITDA normalizzato, range di valore in tre scenari (conservativo, base, potenziale), punteggio di prontezza. Gratis, senza obblighi."
        path="/valutazione"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "GIANO Quanto Vale — Motore di valutazione istantanea",
          "provider": {"@id": "https://gianoholding.com/#organization"},
          "areaServed": "Italy",
          "serviceType": "Business valuation",
          "offers": {"@type": "Offer", "price": "0", "priceCurrency": "EUR"}
        }}
      />
      <section style={{ background: "var(--bone)" }} className="py-20 md:py-28">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Motore di valutazione istantanea</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 980 }}>
            Quanto vale davvero<br />
            <span style={{ color: "var(--terracotta)", fontStyle: "italic" }}>la tua azienda?</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 720, opacity: 0.78, lineHeight: 1.7 }}>
            10 minuti. Bilancio degli ultimi 3 anni + 4 domande qualitative → EBITDA normalizzato, range di valore in tre scenari, punteggio di prontezza e prime leve concrete di crescita del valore.
          </p>
          <Link to="/metodo" className="mt-6 inline-flex items-center gap-2 text-sm font-mono" style={{ color: "var(--terracotta)", textDecoration: "underline", textUnderlineOffset: 4 }} data-testid="link-to-metodo">
            Come funziona il metodo GIANO · 15 controlli e capacità di debito <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FORM */}
      <section className="py-16 md:py-20" style={{ background: "var(--muted)" }}>
        <div className="container-x">
          <form onSubmit={submit} data-testid="valuation-form" className="max-w-3xl mx-auto">
            <div className="card-flat p-8 md:p-10">
              <h3 className="serif-display" style={{ fontSize: 24 }}>1. Anagrafica</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
                <F label="Nome azienda *"><input required data-testid="v-name" className="input-line" value={form.company_name} onChange={e => set("company_name", e.target.value)} /></F>
                <F label="Settore *">
                  <select required data-testid="v-sector" className="input-line" style={{ background: "transparent" }} value={form.sector} onChange={e => set("sector", e.target.value)}>
                    {SECTORS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </F>
                <F label="Regione"><input data-testid="v-region" className="input-line" value={form.region} onChange={e => set("region", e.target.value)} /></F>
                <F label="Dipendenti *"><input required type="number" min="1" data-testid="v-emp" className="input-line" value={form.employees} onChange={e => set("employees", num(e.target.value))} /></F>
              </div>
            </div>

            <div className="card-flat p-8 md:p-10 mt-6">
              <h3 className="serif-display" style={{ fontSize: 24 }}>2. Dati economico-finanziari (M€)</h3>
              <p className="mt-2 text-sm" style={{ opacity: 0.7 }}>Bilancio civilistico degli ultimi 3 esercizi. Se hai solo l'ultimo, va bene comunque.</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="font-mono text-xs mb-3" style={{ color: "var(--terracotta)" }}>ULTIMO ESERCIZIO *</div>
                  <F label="Ricavi (M€)"><input required type="number" step="0.1" min="0" data-testid="v-rev-0" className="input-line" value={form.revenue_current_eur_m} onChange={e => set("revenue_current_eur_m", num(e.target.value))} /></F>
                  <F label="EBITDA (M€)"><input required type="number" step="0.01" data-testid="v-ebitda-0" className="input-line" value={form.ebitda_current_eur_m} onChange={e => set("ebitda_current_eur_m", num(e.target.value))} /></F>
                </div>
                <div>
                  <div className="font-mono text-xs mb-3" style={{ opacity: 0.6 }}>ANNO -1</div>
                  <F label="Ricavi"><input type="number" step="0.1" min="0" data-testid="v-rev-1" className="input-line" value={form.revenue_prev_eur_m} onChange={e => set("revenue_prev_eur_m", num(e.target.value))} /></F>
                  <F label="EBITDA"><input type="number" step="0.01" data-testid="v-ebitda-1" className="input-line" value={form.ebitda_prev_eur_m} onChange={e => set("ebitda_prev_eur_m", num(e.target.value))} /></F>
                </div>
                <div>
                  <div className="font-mono text-xs mb-3" style={{ opacity: 0.6 }}>ANNO -2</div>
                  <F label="Ricavi"><input type="number" step="0.1" min="0" data-testid="v-rev-2" className="input-line" value={form.revenue_prev2_eur_m} onChange={e => set("revenue_prev2_eur_m", num(e.target.value))} /></F>
                  <F label="EBITDA"><input type="number" step="0.01" data-testid="v-ebitda-2" className="input-line" value={form.ebitda_prev2_eur_m} onChange={e => set("ebitda_prev2_eur_m", num(e.target.value))} /></F>
                </div>
              </div>
              <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--border)" }}>
                <F label="PFN — Posizione Finanziaria Netta (M€, positivo se debito)">
                  <input type="number" step="0.01" data-testid="v-pfn" className="input-line" value={form.net_debt_eur_m} onChange={e => set("net_debt_eur_m", num(e.target.value))} />
                </F>
              </div>
            </div>

            <div className="card-flat p-8 md:p-10 mt-6">
              <h3 className="serif-display" style={{ fontSize: 24 }}>3. Fattori qualitativi</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
                <F label="Età del titolare"><input type="number" min="20" max="99" data-testid="v-age" className="input-line" value={form.owner_age} onChange={e => set("owner_age", num(e.target.value))} /></F>
                <F label={`Concentrazione top-3 clienti (${form.top3_customer_concentration_pct}%)`}>
                  <input type="range" min="0" max="100" data-testid="v-conc" value={form.top3_customer_concentration_pct} onChange={e => set("top3_customer_concentration_pct", Number(e.target.value))} style={{ width: "100%" }} />
                </F>
                <F label={`Dipendenza dal fondatore (${form.founder_dependency_score}/5)`}>
                  <input type="range" min="1" max="5" data-testid="v-dep" value={form.founder_dependency_score} onChange={e => set("founder_dependency_score", Number(e.target.value))} style={{ width: "100%" }} />
                  <div className="flex justify-between font-mono text-xs mt-1" style={{ opacity: 0.5 }}>
                    <span>Nessuna</span><span>Totale</span>
                  </div>
                </F>
              </div>
            </div>

            <div className="card-flat p-8 md:p-10 mt-6">
              <h3 className="serif-display" style={{ fontSize: 24 }}>4. Dove ti mandiamo il report?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
                <F label="Nome"><input data-testid="v-cname" className="input-line" value={form.contact_name} onChange={e => set("contact_name", e.target.value)} /></F>
                <F label="Email *"><input required type="email" data-testid="v-email" className="input-line" value={form.email} onChange={e => set("email", e.target.value)} /></F>
              </div>
              <label className="flex items-start gap-3 mt-6 text-sm" style={{ opacity: 0.85 }}>
                <input type="checkbox" data-testid="v-consent" checked={form.consent_marketing} onChange={e => set("consent_marketing", e.target.checked)} style={{ marginTop: 4 }} />
                <span>Voglio ricevere aggiornamenti annuali sul valore della mia azienda e sull'osservatorio successioni.</span>
              </label>
            </div>

            <div className="mt-8 flex items-center gap-6">
              <button type="submit" disabled={status === "loading"} className="btn-primary" data-testid="v-submit">
                {status === "loading" ? "Calcolo in corso..." : "Calcola il valore"} <ArrowRight size={16} />
              </button>
              {status === "error" && <span className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>Errore. Riprova.</span>}
            </div>
          </form>
        </div>
      </section>

      {/* RESULT */}
      {result && (
        <section className="py-16 md:py-24" data-testid="valuation-result">
          <div className="container-x max-w-4xl">
            <div className="overline" style={{ color: "var(--terracotta)" }}>Il tuo report GIANO Analytics</div>
            <h2 className="serif-display mt-4" style={{ fontSize: "clamp(32px, 4vw, 52px)" }}>Il range realistico</h2>
            <p className="mt-4 text-sm" style={{ opacity: 0.7 }}>
              EBITDA normalizzato (media pesata 3 anni): <b>€{result.ebitda_normalized_eur_m}M</b> · margine <b>{result.ebitda_margin_pct}%</b>{result.revenue_cagr_pct !== null && <> · CAGR ricavi <b>{result.revenue_cagr_pct}%</b></>}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-10" style={{ borderTop: "1px solid var(--border)", borderLeft: "1px solid var(--border)" }}>
              <ScenarioBox label="Conservativo" ev={result.enterprise_value_conservative_eur_m} eq={result.equity_value_conservative_eur_m} mult={result.sector_multiple_low} tone="soft" />
              <ScenarioBox label="Base" ev={result.enterprise_value_base_eur_m} eq={result.equity_value_base_eur_m} mult={result.sector_multiple_base} tone="strong" />
              <ScenarioBox label="Potenziale" ev={result.enterprise_value_potential_eur_m} eq={result.equity_value_potential_eur_m} mult={result.sector_multiple_high} tone="soft" />
            </div>

            <div className="mt-10 p-6 md:p-8" style={{ background: "var(--muted)", border: "1px solid var(--border)" }}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="serif-display" style={{ fontSize: 56, lineHeight: 1, color: result.readiness_score >= 70 ? "#065F46" : result.readiness_score >= 40 ? "var(--terracotta)" : "#7f1d1d" }}>
                    {result.readiness_score}
                  </div>
                  <div>
                    <div className="overline">Punteggio di prontezza</div>
                    <div className="text-sm" style={{ opacity: 0.75, marginTop: 4 }}>Su 100. Misura quanto l'azienda è pronta a un processo di cessione.</div>
                  </div>
                </div>
                {result.id && (
                  <div className="flex flex-col md:flex-row gap-2">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/platform/valuation/${result.id}/pdf?lang=it`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="download-pdf-btn"
                      className="btn-primary inline-flex whitespace-nowrap"
                      style={{ background: "var(--midnight)", color: "var(--bone)", borderColor: "var(--midnight)" }}
                    >
                      <Download size={16} /> Report PDF · IT
                    </a>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/platform/valuation/${result.id}/pdf?lang=en`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="download-pdf-btn-en"
                      className="btn-ghost inline-flex whitespace-nowrap"
                      style={{ borderColor: "var(--midnight)" }}
                    >
                      <Download size={16} /> PDF · EN
                    </a>
                  </div>
                )}
              </div>
              {result.id && (
                <p className="mt-4 text-xs font-mono" style={{ opacity: 0.6 }}>
                  PDF brandizzato bilingue (italiano/inglese) con logo GIANO, range in tre scenari, 15 controlli e disclaimer legale — pronto da mostrare in banca, a un advisor o a compratori esteri.
                </p>
              )}
            </div>

            {result.strengths.length > 0 && (
              <div className="mt-10">
                <div className="overline" style={{ color: "#065F46" }}>Punti di forza</div>
                {result.strengths.map((s, i) => (
                  <div key={i} className="flex gap-3 mt-3">
                    <CheckCircle2 size={18} style={{ color: "#065F46", flexShrink: 0, marginTop: 2 }} />
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            )}
            {result.risk_flags.length > 0 && (
              <div className="mt-10">
                <div className="overline" style={{ color: "var(--terracotta)" }}>Leve di crescita del valore</div>
                {result.risk_flags.map((s, i) => (
                  <div key={i} className="flex gap-3 mt-3">
                    <AlertTriangle size={18} style={{ color: "var(--terracotta)", flexShrink: 0, marginTop: 2 }} />
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            )}

            {result.checks_15 && result.checks_15.length > 0 && (
              <div className="mt-12" data-testid="checks-15-block">
                <div className="overline" style={{ color: "var(--terracotta)" }}>15 controlli automatici · Manuale operativo GIANO</div>
                <h3 className="serif-display mt-3" style={{ fontSize: 26 }}>Diagnosi tecnica</h3>
                <p className="mt-2 text-sm" style={{ opacity: 0.7 }}>
                  Verde: nessuna anomalia · Giallo: attenzione · Rosso: verifica urgente · Grigio: dato non ancora disponibile (attivabile con Sale-Ready).
                </p>
                <div className="mt-6 space-y-2">
                  {result.checks_15.map((c) => {
                    const color = c.status === "green" ? "#065F46" : c.status === "yellow" ? "#B45309" : c.status === "red" ? "#7f1d1d" : "#6b7280";
                    return (
                      <div key={c.n} className="flex items-start gap-4 p-4" style={{ background: "var(--card)", borderLeft: `3px solid ${color}` }} data-testid={`check-${c.n}`}>
                        <div className="font-mono text-xs" style={{ color, minWidth: 24 }}>{c.n}</div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{c.name}</div>
                          <div className="text-sm mt-1" style={{ opacity: 0.78, lineHeight: 1.55 }}>{c.message}</div>
                          {c.question && <div className="text-xs mt-2 font-mono" style={{ opacity: 0.6 }}>→ {c.question}</div>}
                        </div>
                        <div className="font-mono text-xs uppercase" style={{ color }}>{c.status}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-16 p-8 md:p-10 flex items-start gap-4" style={{ background: "var(--midnight)", color: "var(--bone)" }}>
              <Info size={20} style={{ flexShrink: 0, marginTop: 4, color: "var(--gold)" }} />
              <div>
                <div className="serif-display" style={{ fontSize: 22 }}>Vuoi un audit approfondito?</div>
                <p className="mt-3 text-sm" style={{ opacity: 0.85, lineHeight: 1.7 }}>
                  Il piano <b>Sale-Ready</b> monitora il valore in continuo (open banking live, checklist documentale, alert). Il piano <b>Giano Verified</b> aggiunge audit da professionista certificato e priorità nel matching.
                </p>
                <a href="/prezzi" className="btn-primary mt-6 inline-flex" style={{ background: "var(--gold)", color: "var(--midnight)", borderColor: "var(--gold)" }}>Vedi i piani →</a>
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="overline" style={{ display: "block", marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function ScenarioBox({ label, ev, eq, mult, tone }) {
  const isStrong = tone === "strong";
  return (
    <div className="p-6 md:p-8" style={{
      borderRight: "1px solid var(--border)",
      borderBottom: "1px solid var(--border)",
      background: isStrong ? "var(--midnight)" : "var(--card)",
      color: isStrong ? "var(--bone)" : "inherit",
    }}>
      <div className="font-mono text-xs" style={{ color: isStrong ? "var(--gold)" : "var(--terracotta)" }}>{label.toUpperCase()}</div>
      <div className="serif-display mt-4" style={{ fontSize: 40, lineHeight: 1 }}>€{ev.toFixed(1)}M</div>
      <div className="text-xs mt-2 font-mono" style={{ opacity: 0.7 }}>Enterprise Value · {mult}x EBITDA</div>
      <div className="mt-6 pt-4" style={{ borderTop: isStrong ? "1px solid rgba(247,245,240,0.2)" : "1px solid var(--border)" }}>
        <div className="text-xs font-mono" style={{ opacity: 0.65 }}>Equity (netto PFN)</div>
        <div className="serif-display mt-1" style={{ fontSize: 24 }}>€{eq.toFixed(1)}M</div>
      </div>
    </div>
  );
}
